"use client";

import { useSession } from "@/contexts/sessionContext";
import { NEXT_PUBLIC_SEGMENT_ANALYTIC_KEY } from "@/modules/config";
import { logger } from "@/modules/lib/observability/logger";
import { noRefetchConfig } from "@/modules/query/QueryProvider";
import type { User } from "@/modules/session/types";
import {
  sendAnalyticsEvent as clientAnalyticsEvent,
  initAnalytics,
  enrichAnalyticsUser as packageEnrichAnalyticsUser,
  sendPageAnalyticsEvent,
} from "@devsbb/analytics-client";
import { useQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useRef } from "react";
import * as CookieConsent from "vanilla-cookieconsent";
import { useStore } from "./useStore";

export function useAnalyticsEvents() {
  const { store, storeId } = useStore();
  const { user } = useSession();
  const locale = useLocale();

  const isInitialised = useRef(false);

  useEffect(() => {
    if (!NEXT_PUBLIC_SEGMENT_ANALYTIC_KEY || isInitialised.current) return;
    initAnalytics({
      segmentWriteKey: NEXT_PUBLIC_SEGMENT_ANALYTIC_KEY,
      app: { name: "pos-ui" },
    });
    isInitialised.current = true;
  }, []);

  const enrichAnalyticsUser = useCallback(async (user: User | undefined) => {
    const preferences = CookieConsent.getUserPreferences();
    const userHasAcceptedAnalytics =
      preferences.acceptedCategories.includes("analytics");

    if (!isInitialised.current) return;
    if (!user) return;
    if (!userHasAcceptedAnalytics) return;

    try {
      await packageEnrichAnalyticsUser({
        "Employee Email": `${user.email}`,
        id: `${user.id}`,
      });
    } catch (error) {
      logger.error(
        "(useAnalyticsEvent:enrichAnalyticsUser): failed to identify user in Segment",
        { userId: user.id },
        error instanceof Error ? error : undefined,
      );
    }
  }, []);

  useQuery({
    queryKey: ["analyticsUser", user?.id],
    queryFn: async () => {
      await enrichAnalyticsUser(user);
      return user;
    },
    enabled: !!user?.id,
    staleTime: Number.POSITIVE_INFINITY,
    ...noRefetchConfig,
  });

  const sendPageViewEvent = useCallback(
    (pageType: string, extraProp?: Record<string, unknown>) => {
      sendPageAnalyticsEvent("Page Viewed", {
        page_type: pageType,
        store: store,
        store_id: storeId ? Number(storeId) : null,
        locale: locale,
        ...extraProp,
      });
    },
    [store, storeId, locale],
  );

  const sendTapComponentEvent = useCallback(
    (component: string, extraProp?: Record<string, unknown>) => {
      clientAnalyticsEvent("Component Tapped", {
        component: component,
        store: store,
        store_id: storeId ? Number(storeId) : "",
        locale: locale,
        ...extraProp,
      });
    },
    [store, storeId, locale],
  );

  const sendAnalyticsEvent = useCallback(
    (eventName: string, extraProp?: Record<string, unknown>) => {
      clientAnalyticsEvent(eventName, {
        store: store,
        store_id: storeId ? Number(storeId) : "",
        locale: locale,
        ...extraProp,
      });
    },
    [store, storeId, locale],
  );

  return {
    sendPageViewEvent,
    sendTapComponentEvent,
    sendAnalyticsEvent,
    enrichAnalyticsUser,
  };
}
